import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, X, Plus } from "lucide-react";

const GRADES = ["IX", "X", "XI", "XII"];
const STREAMS = ["Science (PCM)", "Science (PCB)", "Science (PCMB)", "Commerce", "Humanities/Arts", "Undecided"];
const SUBJECT_POOL = ["Math","Physics","Chemistry","Biology","English","Hindi","Computer Science","Economics","Business Studies","Accountancy","History","Political Science","Psychology","Geography","Sanskrit","Physical Education","Art","Music"];
const INTEREST_POOL = ["Technology","Design","Sports","Reading","Writing","Music","Debating","Gaming","Robotics","Public Speaking","Coding","Startups","Science Experiments","Social Work","Photography","Filmmaking","Health & Medicine","Law & Justice","Finance","Teaching"];
const STRENGTH_POOL = ["Analytical","Creative","Leadership","Communication","Teamwork","Problem Solving","Organized","Empathetic","Persistent","Curious","Adaptable","Detail-oriented"];
const REGIONS = ["India", "Global", "Both"];

export default function Onboarding() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    grade: "XI", stream: "", subjects: [], scores: {}, interests: [], strengths: [], region_pref: "Both", goals: "",
  });

  useEffect(() => {
    api.get("/profile/").then((r) => {
      if (r.data && r.data.grade) setProfile((p) => ({ ...p, ...r.data }));
    }).finally(() => setLoading(false));
  }, []);

  const toggle = (key, val) => {
    setProfile((p) => ({
      ...p,
      [key]: p[key].includes(val) ? p[key].filter((x) => x !== val) : [...p[key], val],
    }));
  };

  const toggleSubject = (sub) => {
    setProfile((p) => {
      const has = p.subjects.includes(sub);
      const subjects = has ? p.subjects.filter((s) => s !== sub) : [...p.subjects, sub];
      const scores = { ...p.scores };
      if (has) delete scores[sub];
      return { ...p, subjects, scores };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile/", profile);
      toast.success("Profile saved!");
      nav("/quiz");
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <AppShell><div className="p-10 text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="onboarding-page">
        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">Step 1</p>
        <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Tell us about you</h1>
        <p className="text-slate-400 mt-2 max-w-xl">The more we know, the sharper your career roadmap. All fields optional.</p>

        <div className="mt-8 brimi-card p-6 space-y-6">
          {/* Grade */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Grade</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {GRADES.map((g) => (
                <button key={g} data-testid={`grade-${g}`}
                  onClick={() => setProfile({ ...profile, grade: g })}
                  className={`px-4 py-2 rounded-full text-sm font-bold border transition ${
                    profile.grade === g ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}>
                  Class {g}
                </button>
              ))}
            </div>
          </div>

          {/* Stream */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Stream</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {STREAMS.map((s) => (
                <button key={s} data-testid={`stream-${s}`}
                  onClick={() => setProfile({ ...profile, stream: s })}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                    profile.stream === s ? "text-slate-900" : "border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                  style={profile.stream === s ? { background: "#FBBF24", borderColor: "#FBBF24" } : {}}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Subjects */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Subjects & Scores (%)</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {SUBJECT_POOL.map((s) => {
                const active = profile.subjects.includes(s);
                return (
                  <button key={s} data-testid={`subject-${s}`}
                    onClick={() => toggleSubject(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${active ? "bg-electric-500 text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                    style={active ? { background: "#3B82F6", borderColor: "#3B82F6" } : {}}>
                    {active ? "✓ " : "+ "}{s}
                  </button>
                );
              })}
            </div>

            {profile.subjects.length > 0 && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {profile.subjects.map((s) => (
                  <div key={s} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200">
                    <span className="text-sm font-semibold text-slate-800 flex-1 truncate">{s}</span>
                    <input
                      data-testid={`score-${s}`}
                      type="number" min={0} max={100} step={1}
                      value={profile.scores[s] ?? ""}
                      onChange={(e) => setProfile((p) => ({ ...p, scores: { ...p.scores, [s]: e.target.value ? Number(e.target.value) : "" } }))}
                      placeholder="0-100"
                      className="w-20 text-center border border-slate-200 rounded-lg px-2 py-1 text-slate-900 outline-none focus:ring-2 focus:ring-blue-100"
                    />
                    <span className="text-slate-400 text-xs">%</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Interests */}
          <ChipSection
            label="Interests"
            pool={INTEREST_POOL}
            values={profile.interests}
            onToggle={(v) => toggle("interests", v)}
            color="#FB7185"
            prefix="interest"
          />

          {/* Strengths */}
          <ChipSection
            label="Strengths"
            pool={STRENGTH_POOL}
            values={profile.strengths}
            onToggle={(v) => toggle("strengths", v)}
            color="#34D399"
            prefix="strength"
          />

          {/* Region */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">College Region Preference</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {REGIONS.map((r) => (
                <button key={r} data-testid={`region-${r}`}
                  onClick={() => setProfile({ ...profile, region_pref: r })}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                    profile.region_pref === r ? "text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                  style={profile.region_pref === r ? { background: "#3B82F6", borderColor: "#3B82F6" } : {}}>
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* Goals */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Career Goals (optional)</label>
            <textarea
              data-testid="goals-textarea"
              value={profile.goals || ""}
              onChange={(e) => setProfile({ ...profile, goals: e.target.value })}
              rows={3}
              placeholder="e.g., I want to work in AI research; I love architecture and want to design homes..."
              className="mt-2 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div className="flex justify-end pt-2">
            <button data-testid="onboarding-save-button" onClick={save} disabled={saving} className="brimi-pill accent-coral flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save & Continue to Quiz
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function ChipSection({ label, pool, values, onToggle, color, prefix }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">{label}</label>
      <div className="mt-2 flex flex-wrap gap-2">
        {pool.map((v) => {
          const active = values.includes(v);
          return (
            <button key={v} data-testid={`${prefix}-${v}`}
              onClick={() => onToggle(v)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${active ? "text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
              style={active ? { background: color, borderColor: color } : {}}>
              {active ? "✓ " : "+ "}{v}
            </button>
          );
        })}
      </div>
    </div>
  );
}
